package fr.frux.core.database

import app.cash.sqldelight.Query
import app.cash.sqldelight.TransacterImpl
import app.cash.sqldelight.db.QueryResult
import app.cash.sqldelight.db.SqlCursor
import app.cash.sqldelight.db.SqlDriver
import kotlin.Any
import kotlin.Double
import kotlin.Long
import kotlin.String
import kotlin.collections.Collection

public class SiteQueries(
  driver: SqlDriver,
) : TransacterImpl(driver) {
  public fun <T : Any> selectInBounds(
    latMin: Double,
    latMax: Double,
    lngMin: Double,
    lngMax: Double,
    minCachedAt: Long,
    mapper: (
      id: Long,
      name: String,
      description: String?,
      latitude: Double,
      longitude: Double,
      access_rule: String,
      manager_name: String?,
      has_owner: Long,
      trees_count: Long,
      created_by: Long,
      created_by_username: String?,
      created_at: Long?,
      cached_at: Long,
      next_harvest_id: Long?,
      next_harvest_at: Long?,
      next_harvest_hosted: Long?,
    ) -> T,
  ): Query<T> = SelectInBoundsQuery(latMin, latMax, lngMin, lngMax, minCachedAt) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2),
      cursor.getDouble(3)!!,
      cursor.getDouble(4)!!,
      cursor.getString(5)!!,
      cursor.getString(6),
      cursor.getLong(7)!!,
      cursor.getLong(8)!!,
      cursor.getLong(9)!!,
      cursor.getString(10),
      cursor.getLong(11),
      cursor.getLong(12)!!,
      cursor.getLong(13),
      cursor.getLong(14),
      cursor.getLong(15)
    )
  }

  public fun selectInBounds(
    latMin: Double,
    latMax: Double,
    lngMin: Double,
    lngMax: Double,
    minCachedAt: Long,
  ): Query<Site> = selectInBounds(latMin, latMax, lngMin, lngMax, minCachedAt, ::Site)

  public fun <T : Any> selectById(id: Long, mapper: (
    id: Long,
    name: String,
    description: String?,
    latitude: Double,
    longitude: Double,
    access_rule: String,
    manager_name: String?,
    has_owner: Long,
    trees_count: Long,
    created_by: Long,
    created_by_username: String?,
    created_at: Long?,
    cached_at: Long,
    next_harvest_id: Long?,
    next_harvest_at: Long?,
    next_harvest_hosted: Long?,
  ) -> T): Query<T> = SelectByIdQuery(id) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2),
      cursor.getDouble(3)!!,
      cursor.getDouble(4)!!,
      cursor.getString(5)!!,
      cursor.getString(6),
      cursor.getLong(7)!!,
      cursor.getLong(8)!!,
      cursor.getLong(9)!!,
      cursor.getString(10),
      cursor.getLong(11),
      cursor.getLong(12)!!,
      cursor.getLong(13),
      cursor.getLong(14),
      cursor.getLong(15)
    )
  }

  public fun selectById(id: Long): Query<Site> = selectById(id, ::Site)

  public fun <T : Any> selectPlantingsOf(siteIds: Collection<Long>, mapper: (
    id: Long,
    site_id: Long,
    species_id: Long,
    species_common_name_fr: String,
    species_scientific_name: String,
    species_form: String,
    count_estimate: String,
    note: String?,
  ) -> T): Query<T> = SelectPlantingsOfQuery(siteIds) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getLong(1)!!,
      cursor.getLong(2)!!,
      cursor.getString(3)!!,
      cursor.getString(4)!!,
      cursor.getString(5)!!,
      cursor.getString(6)!!,
      cursor.getString(7)
    )
  }

  public fun selectPlantingsOf(siteIds: Collection<Long>): Query<Site_planting> = selectPlantingsOf(siteIds, ::Site_planting)

  public fun countAll(): Query<Long> = Query(-700_204_204, arrayOf("site"), driver, "site.sq", "countAll", "SELECT COUNT(*) FROM site") { cursor ->
    cursor.getLong(0)!!
  }

  /**
   * @return The number of rows updated.
   */
  public fun upsertSite(
    id: Long?,
    name: String,
    description: String?,
    latitude: Double,
    longitude: Double,
    access_rule: String,
    manager_name: String?,
    has_owner: Long,
    trees_count: Long,
    created_by: Long,
    created_by_username: String?,
    created_at: Long?,
    cached_at: Long,
    next_harvest_id: Long?,
    next_harvest_at: Long?,
    next_harvest_hosted: Long?,
  ): QueryResult<Long> {
    val result = driver.execute(1_764_740_568, """
        |INSERT OR REPLACE INTO site(
        |  id, name, description, latitude, longitude, access_rule, manager_name, has_owner,
        |  trees_count, created_by, created_by_username, created_at, cached_at,
        |  next_harvest_id, next_harvest_at, next_harvest_hosted
        |) VALUES (
        |  ?, ?, ?, ?, ?, ?, ?, ?,
        |  ?, ?, ?, ?, ?,
        |  ?, ?, ?
        |)
        """.trimMargin(), 16) {
          var parameterIndex = 0
          bindLong(parameterIndex++, id)
          bindString(parameterIndex++, name)
          bindString(parameterIndex++, description)
          bindDouble(parameterIndex++, latitude)
          bindDouble(parameterIndex++, longitude)
          bindString(parameterIndex++, access_rule)
          bindString(parameterIndex++, manager_name)
          bindLong(parameterIndex++, has_owner)
          bindLong(parameterIndex++, trees_count)
          bindLong(parameterIndex++, created_by)
          bindString(parameterIndex++, created_by_username)
          bindLong(parameterIndex++, created_at)
          bindLong(parameterIndex++, cached_at)
          bindLong(parameterIndex++, next_harvest_id)
          bindLong(parameterIndex++, next_harvest_at)
          bindLong(parameterIndex++, next_harvest_hosted)
        }
    notifyQueries(1_764_740_568) { emit ->
      emit("site")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public fun deletePlantingsOf(site_id: Long): QueryResult<Long> {
    val result = driver.execute(145_852_166, """DELETE FROM site_planting WHERE site_id = ?""", 1) {
          var parameterIndex = 0
          bindLong(parameterIndex++, site_id)
        }
    notifyQueries(145_852_166) { emit ->
      emit("site_planting")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public fun insertPlanting(
    id: Long?,
    site_id: Long,
    species_id: Long,
    species_common_name_fr: String,
    species_scientific_name: String,
    species_form: String,
    count_estimate: String,
    note: String?,
  ): QueryResult<Long> {
    val result = driver.execute(1_649_532_722, """
        |INSERT OR REPLACE INTO site_planting(
        |  id, site_id, species_id, species_common_name_fr, species_scientific_name, species_form,
        |  count_estimate, note
        |) VALUES (
        |  ?, ?, ?, ?, ?, ?,
        |  ?, ?
        |)
        """.trimMargin(), 8) {
          var parameterIndex = 0
          bindLong(parameterIndex++, id)
          bindLong(parameterIndex++, site_id)
          bindLong(parameterIndex++, species_id)
          bindString(parameterIndex++, species_common_name_fr)
          bindString(parameterIndex++, species_scientific_name)
          bindString(parameterIndex++, species_form)
          bindString(parameterIndex++, count_estimate)
          bindString(parameterIndex++, note)
        }
    notifyQueries(1_649_532_722) { emit ->
      emit("site_planting")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public fun deleteById(id: Long): QueryResult<Long> {
    val result = driver.execute(-54_191_905, """DELETE FROM site WHERE id = ?""", 1) {
          var parameterIndex = 0
          bindLong(parameterIndex++, id)
        }
    notifyQueries(-54_191_905) { emit ->
      emit("site")
      emit("site_planting")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public fun deleteExpired(threshold: Long): QueryResult<Long> {
    val result = driver.execute(-1_148_144_904, """DELETE FROM site WHERE cached_at < ?""", 1) {
          var parameterIndex = 0
          bindLong(parameterIndex++, threshold)
        }
    notifyQueries(-1_148_144_904) { emit ->
      emit("site")
      emit("site_planting")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public fun deleteOldestOverflow(n: Long): QueryResult<Long> {
    val result = driver.execute(2_116_554_734, """
        |DELETE FROM site WHERE id IN (
        |  SELECT id FROM site ORDER BY cached_at ASC LIMIT ?
        |)
        """.trimMargin(), 1) {
          var parameterIndex = 0
          bindLong(parameterIndex++, n)
        }
    notifyQueries(2_116_554_734) { emit ->
      emit("site")
      emit("site_planting")
    }
    return result
  }

  private inner class SelectInBoundsQuery<out T : Any>(
    public val latMin: Double,
    public val latMax: Double,
    public val lngMin: Double,
    public val lngMax: Double,
    public val minCachedAt: Long,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("site", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("site", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> = driver.executeQuery(-1_442_579_176, """
    |SELECT site.id, site.name, site.description, site.latitude, site.longitude, site.access_rule, site.manager_name, site.has_owner, site.trees_count, site.created_by, site.created_by_username, site.created_at, site.cached_at, site.next_harvest_id, site.next_harvest_at, site.next_harvest_hosted FROM site
    |WHERE latitude BETWEEN ? AND ?
    |  AND longitude BETWEEN ? AND ?
    |  AND cached_at >= ?
    """.trimMargin(), mapper, 5) {
      var parameterIndex = 0
      bindDouble(parameterIndex++, latMin)
      bindDouble(parameterIndex++, latMax)
      bindDouble(parameterIndex++, lngMin)
      bindDouble(parameterIndex++, lngMax)
      bindLong(parameterIndex++, minCachedAt)
    }

    override fun toString(): String = "site.sq:selectInBounds"
  }

  private inner class SelectByIdQuery<out T : Any>(
    public val id: Long,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("site", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("site", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> = driver.executeQuery(820_230_064, """SELECT site.id, site.name, site.description, site.latitude, site.longitude, site.access_rule, site.manager_name, site.has_owner, site.trees_count, site.created_by, site.created_by_username, site.created_at, site.cached_at, site.next_harvest_id, site.next_harvest_at, site.next_harvest_hosted FROM site WHERE id = ?""", mapper, 1) {
      var parameterIndex = 0
      bindLong(parameterIndex++, id)
    }

    override fun toString(): String = "site.sq:selectById"
  }

  private inner class SelectPlantingsOfQuery<out T : Any>(
    public val siteIds: Collection<Long>,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("site_planting", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("site_planting", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> {
      val siteIdsIndexes = createArguments(count = siteIds.size)
      return driver.executeQuery(null, """SELECT site_planting.id, site_planting.site_id, site_planting.species_id, site_planting.species_common_name_fr, site_planting.species_scientific_name, site_planting.species_form, site_planting.count_estimate, site_planting.note FROM site_planting WHERE site_id IN $siteIdsIndexes""", mapper, siteIds.size) {
            var parameterIndex = 0
            siteIds.forEach { siteIds_ ->
              bindLong(parameterIndex++, siteIds_)
            }
          }
    }

    override fun toString(): String = "site.sq:selectPlantingsOf"
  }
}
